import { z } from 'zod'

import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import { alertsQueryOptions } from '@/features/alerts/api/queries'
import { watchlistCollectionQueryOptions } from '@/features/watchlist/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import { alertTabSchema } from '@/schemas/alerts'

/**
 * Here rather than in AlertsScreen.tsx because the loader below reads the
 * tab through it too, and the route's `validateSearch` and the loader must
 * not be able to disagree about which tab a URL means.
 */
export const alertsSearchSchema = z.object({
  symbol: z.string().optional(),
  // The sidebar's ALERTS group is the switcher now (`alertsGroup()`,
  // `nav-groups.ts`) — a bare `/alerts` or a stale `?status=` both land on
  // Active rather than throwing a router error.
  status: alertTabSchema.default('active').catch('active'),
})

/**
 * The `/alerts` loader.
 *
 * MAIN: the alerts on the tab the URL opens, awaited — the table is the page.
 *
 * SECONDARY, started and not awaited, on a real navigation only (a hover
 * preload loads the table and nothing else): the member's watchlists, which
 * only feed the edit form's company picker, and which the sidebar has
 * usually cached already.
 *
 * `?status=` is read from `location.search` through the route's own schema,
 * not through `loaderDeps`: a dep would make every switch between Active and
 * Expired a new route match — the full-page pending skeleton on each click.
 * As it is, a switch runs no loader at all; the screen keeps the previous
 * tab's rows on screen, dimmed, until the next tab's land.
 *
 * Its own module, as `app.guard.ts` is, so a test can mount it under a root
 * of its own. It imports from `api/` modules and schemas only — route
 * loaders are not code-split, so anything imported here ships in the entry
 * chunk.
 */
export async function loadAlerts({
  context: { queryClient },
  location,
  preload,
}: {
  context: RouterContext
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  if (!preload) prefetchRouteData(queryClient, watchlistCollectionQueryOptions())
  const { status } = alertsSearchSchema.parse(location.search)
  await ensureRouteData(queryClient, alertsQueryOptions(status))
}
